import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Slide,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import Editor, { OnMount } from '@monaco-editor/react';
import {
  CloudUploadOutlined,
  Close,
  History,
  ImageOutlined,
  OpenInNew,
  SaveOutlined,
} from '@mui/icons-material';
import { fetchData, postData } from '../../helpers/apiUtils';
import { FullProfile, ProfileVersion, STATUS_STYLES } from './types';
import { EDITOR_HELP, HelpLegend, InfoHint } from './help';
import CardEditor, { CardData, normalizeCardData, uploadCardImage } from './CardEditor';

// Lazy: BlockNote + Mantine only load when the (flag-gated) Blocks tab is opened.
const BlocksEditor = React.lazy(() => import('./blocks/BlocksEditor'));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface Draft {
  status: string;
  page_title: string;
  affiliate_url: string;
  content_html: string;
  content_css: string;
  // The overview card (JSON). Edited via the Übersichtskarte tab; compared by JSON value.
  card_data: CardData;
}

const asCardData = (v: unknown): CardData =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as CardData) : {};

const draftFromProfile = (p: FullProfile): Draft => ({
  status: p.status,
  page_title: p.page_title ?? '',
  affiliate_url: p.affiliate_url ?? '',
  content_html: p.content_html ?? '',
  content_css: p.content_css ?? '',
  card_data: asCardData(p.card_data),
});

interface Props {
  profileId: number | null;
  onClose: () => void;
  onChanged: () => void; // refresh the matrix
  /** Öffnet den Dialog „Affiliate-Links" für diesen Broker — der EINE Schreibweg für Links. */
  onAffiliateLinks?: (brokerId: number, brokerName: string) => void;
  /** Zählt hoch, wenn jener Dialog gespeichert/ausgespielt hat — dann ist dieser Stand veraltet. */
  affiliateStand?: number;
}

/** Ausspiel-Zustand dieser Seite (GET /profiles/:id/ausspiel-zustand). */
type AusspielZustand = {
  zustand:
    | 'aktuell'
    | 'veraltet'
    | 'nie_ausgespielt'
    | 'karte_offen'
    | 'kein_profil'
    | 'nicht_live'
    | null;
  ausgespieltAm: string | null;
};
const NICHT_AUSGESPIELT_TEXT: Record<string, string> = {
  veraltet: 'Der gespeicherte Stand ist noch nicht auf dem Portal. „Ausspielen" klicken.',
  nie_ausgespielt: 'Status live, aber noch nie ausgespielt — die Seite steht nicht auf dem Portal.',
  karte_offen: 'Seite ausgespielt, die Übersichtskarte aber nicht übernommen. Erneut ausspielen.',
};

/**
 * Fullscreen split editor: Monaco (HTML/CSS) on the left, a live preview on the
 * right that loads the portal's real CSS (base + brand override) so it matches the
 * deployed page. Save is versioned; Deploy pushes to the portal; rollback restores.
 */
const ProfileEditorDialog: React.FC<Props> = ({
  profileId,
  onClose,
  onChanged,
  onAffiliateLinks,
  affiliateStand = 0,
}) => {
  const [profile, setProfile] = useState<FullProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [tab, setTab] = useState<'html' | 'css' | 'fields' | 'card' | 'versions' | 'blocks'>(
    'html',
  );
  // Blocks-editor flag for the current user (fetched once per dialog lifetime; null = pending).
  const [blocksAllowed, setBlocksAllowed] = useState<boolean | null>(null);
  // Once visited, the Blocks tab stays MOUNTED (hidden via display) so switching tabs
  // doesn't discard unsaved blocks — the BlockNote state lives inside the editor.
  const [blocksMounted, setBlocksMounted] = useState(false);
  const [versions, setVersions] = useState<ProfileVersion[]>([]);
  const [portalCss, setPortalCss] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmTakedown, setConfirmTakedown] = useState(false);
  /**
   * Reported upward by BlocksEditor — the dialog cannot see BlockNote's document itself.
   *
   * Held TWICE on purpose: **state for rendering, ref for the guard.**
   *
   * The properties panel commits a field edit on blur, so a close gesture that also blurs the
   * field puts the commit and the close in the same flow. `setBlocksDirty` is an async React
   * update; a guard reading the STATE in that flow can still see "clean" and close without a
   * prompt. The ref updates synchronously, so it cannot.
   *
   * ⚠ Honest scope, measured 2026-08-04: this is HARDENING, not a bug fix. The close paths that
   * exist today are safe without it — clicking the X blurs on `mousedown` and closes on `click`,
   * which are separate discrete events, and React flushes in between (verified: the discard
   * prompt appears with the ref AND with plain state). The one path that DID lose work was the
   * panel's own Escape handler, which called `blur()` and let the event bubble to the Dialog —
   * all inside ONE handler, so no flush boundary. That is fixed at the source in
   * BlockPropsPanel (Escape no longer blurs and no longer bubbles).
   *
   * The ref stays because the next handler that commits and closes in one flow would otherwise
   * reintroduce silent loss, and that failure is invisible until someone loses work.
   */
  const [blocksDirty, setBlocksDirty] = useState(false);
  const blocksDirtyRef = useRef(false);
  const setBlocksDirtyBoth = useCallback((dirty: boolean) => {
    blocksDirtyRef.current = dirty;
    setBlocksDirty(dirty);
  }, []);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // Steht der gespeicherte Stand auf dem Portal? Dauerhaft angezeigt, nicht nur als Meldung nach dem
  // Speichern — Christian hat am 2026-10-20 fünf Profile gespeichert und nicht ausgespielt, ohne
  // dass ihm das irgendwo stehen blieb.
  const [ausspiel, setAusspiel] = useState<AusspielZustand | null>(null);
  // Der Dialog lebt über mehrere Profile hinweg: eine langsame Antwort für das VORIGE Profil darf
  // den Zustand des jetzigen nicht überschreiben. Nur die jüngste Anfrage wird übernommen.
  const ausspielAnfrage = useRef(0);
  const ladeAusspiel = useCallback(async (id: number) => {
    const nr = ++ausspielAnfrage.current;
    const r = await fetchData<AusspielZustand>(`/api/broker-hub/profiles/${id}/ausspiel-zustand`);
    if (nr !== ausspielAnfrage.current) return;
    setAusspiel(r.success && r.data ? r.data : null);
  }, []);
  const [message, setMessage] = useState<{
    severity: 'success' | 'warning' | 'error';
    text: string;
  } | null>(null);
  // Debounced copy of the draft that actually drives the (expensive) iframe re-render.
  const [previewDraft, setPreviewDraft] = useState<Draft | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Profil-Id, für die der Startreiter schon automatisch gesetzt wurde (einmal je Öffnen). */
  const autoReiterFuer = useRef<number | null>(null);
  // Der HTML-Reiter eines BLOCKS-Profils zeigt den Server-Render, nicht `content_html`.
  //
  // ⚠ WARUM NICHT `content_html`. Diese Spalte ist bei einem blocks-gepflegten Profil der
  // eingefrorene Import von VOR der Umstellung — der Deploy schreibt sie nie zurück
  // (`BrokerHubDeployService.ts` § "Rendered HTML is deliberately NOT written back"). Gemessen auf
  // Prod 2026-08-10: `content_html` enthält `<script` und 0× `bh-blocks`, das ausgelieferte
  // `_broker_hub_html` 0× `<script` und 244× `bh-blocks`. Der Reiter zeigte also Code, den es auf
  // der Seite nicht gibt — und der Hinweis darüber behauptete, das HTML sei generiert.
  //
  // Gerendert wird der GESPEICHERTE Stand (kein `blocks` im Rumpf): das ist exakt das, was ein
  // Deploy in diesem Moment verschicken würde. Die Entwurfs-Blöcke liegen ohnehin in `BlocksEditor`
  // und sind von hier nicht erreichbar; für "was geht live" wären sie auch die falsche Quelle.
  const [renderedHtml, setRenderedHtml] = useState<string | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [renderBusy, setRenderBusy] = useState(false);
  // `${id}:${revision}` des zuletzt gerenderten Standes — der Render forkt serverseitig einen
  // Kindprozess, also nicht bei jedem Reiterwechsel neu holen.
  const renderedForRef = useRef<string | null>(null);
  // HTML-editor image upload: keep the Monaco instance so we can insert an <img> at the cursor.
  const htmlEditorRef = useRef<Parameters<OnMount>[0] | null>(null);
  const htmlImgFileRef = useRef<HTMLInputElement | null>(null);
  const [uploadingImg, setUploadingImg] = useState(false);

  const onHtmlImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (htmlImgFileRef.current) htmlImgFileRef.current.value = '';
    if (!file) return;
    setUploadingImg(true);
    setMessage(null);
    try {
      const url = await uploadCardImage(file);
      const snippet = `<img src="${url}" alt="" />`;
      const ed = htmlEditorRef.current;
      if (ed) {
        // Insert at the cursor; the model change fires the Editor onChange → draft stays in sync.
        const sel = ed.getSelection();
        if (sel)
          ed.executeEdits('broker-hub-image', [
            { range: sel, text: snippet, forceMoveMarkers: true },
          ]);
        ed.focus();
      } else {
        // Editor not mounted (e.g. never opened the HTML tab) — append to the draft.
        setDraft((prev) =>
          prev ? { ...prev, content_html: `${prev.content_html ?? ''}${snippet}` } : prev,
        );
      }
      setMessage({ severity: 'success', text: 'Bild hochgeladen und als <img> eingefügt.' });
    } catch (err) {
      setMessage({ severity: 'error', text: (err as Error).message || 'Upload fehlgeschlagen' });
    } finally {
      setUploadingImg(false);
    }
  };

  const load = useCallback(
    async (id: number) => {
      setLoading(true);
      setMessage(null);
      setChangeNote('');
      setTab('html');
      setBlocksMounted(false);
      // Zurücksetzen, sonst greift die Blocks-Umschaltung beim ZWEITEN Öffnen desselben Profils
      // nicht mehr (die Id ist dieselbe, der Ref hielte sie noch) — und `load()` hat gerade auf
      // HTML gestellt, der Dialog bliebe also auf der Nur-Lese-Ansicht stehen.
      autoReiterFuer.current = null;
      // Vor dem Abruf leeren: sonst stünde der Zustand des vorher geöffneten Profils hier.
      setAusspiel(null);
      void ladeAusspiel(id);
      // Explicit, although unmounting BlocksEditor also reports false: a stale `true` would raise a
      // discard prompt on a profile the author has not touched, and the ref survives a re-render.
      setBlocksDirtyBoth(false);
      const [p, css] = await Promise.all([
        fetchData<FullProfile>(`/api/broker-hub/profiles/${id}`),
        fetchData<{ css: string }>(`/api/broker-hub/profiles/${id}/portal-css`),
      ]);
      if (p.success && p.data) {
        setProfile(p.data);
        const d = draftFromProfile(p.data);
        setDraft(d);
        setPreviewDraft(d);
        fetchData<ProfileVersion[]>(`/api/broker-hub/profiles/${id}/versions`).then((v) =>
          setVersions(v.success && v.data ? v.data : []),
        );
      }
      setPortalCss(css.success && css.data ? css.data.css : '');
      setLoading(false);
      // setBlocksDirtyBoth is itself a useCallback([]) and therefore stable — listed so the dep
      // array stays honest rather than relying on that being remembered.
    },
    [setBlocksDirtyBoth, ladeAusspiel],
  );

  useEffect(() => {
    if (profileId != null) load(profileId);
    else {
      setProfile(null);
      setDraft(null);
      setVersions([]);
    }
  }, [profileId, load]);

  // Blocks-editor access: fetched once (first dialog open), cached for the component lifetime.
  useEffect(() => {
    if (profileId == null || blocksAllowed !== null) return;
    let cancelled = false;
    fetchData<{ allowed: boolean; role: string }>('/api/broker-hub/blocks-editor-access').then(
      (r) => {
        if (!cancelled) setBlocksAllowed(!!(r.success && r.data?.allowed));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [profileId, blocksAllowed]);

  // Ein Blocks-Profil auf dem Blocks-Reiter öffnen, nicht auf HTML.
  //
  // Der HTML-Reiter ist bei `content_format='blocks'` eine NUR-LESE-Renderansicht; die einzige
  // Bearbeitungsfläche ist „Blocks". Bei einer frisch angelegten Vergleichsseite stand dort
  // „Kein Inhalt — im HTML-Tab einfügen", also eine Anweisung, die ins Leere führt.
  //
  // ⚠ Warum ein eigener Effekt und nicht `setTab('blocks')` in `load()`: `blocksAllowed` kommt aus
  // einem SEPARATEN Fetch (oben) und gated das <Tab>-Element selbst — ein früher gesetzter Wert
  // zeigte auf einen Reiter, den es noch nicht gibt. Und `setBlocksMounted(true)` passiert sonst
  // ausschliesslich im onChange der Tabs, also nur bei einem echten Klick; ohne ihn ist die
  // Renderbedingung des Editors falsch und der Dialog stünde mit gewähltem Reiter und LEEREM
  // Körper da — schlechter als der Fehler, den das hier behebt.
  useEffect(() => {
    if (profileId == null || !profile || blocksAllowed !== true) return;
    // Nur einmal je geöffnetem Profil: sonst würde ein Reiterwechsel des Benutzers sofort
    // zurückgesetzt, sobald irgendein Zustand oben neu rendert.
    if (autoReiterFuer.current === profileId) return;
    autoReiterFuer.current = profileId;
    if (profile.content_format !== 'blocks') return;
    setTab('blocks');
    setBlocksMounted(true);
  }, [profileId, profile, blocksAllowed]);

  // Debounce preview updates so Monaco keystrokes don't thrash the iframe.
  useEffect(() => {
    if (!draft) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setPreviewDraft(draft), 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [draft]);

  const isDirty = useMemo(() => {
    if (!profile || !draft) return false;
    const base = draftFromProfile(profile);
    return (Object.keys(base) as (keyof Draft)[]).some((k) =>
      k === 'card_data'
        ? JSON.stringify(base.card_data) !== JSON.stringify(draft.card_data)
        : base[k] !== draft[k],
    );
  }, [profile, draft]);

  /**
   * Unsaved work anywhere in the dialog — the Draft fields OR the block document.
   *
   * `isDirty` only ever saw the Draft. The block document lives inside BlockNote until its own
   * Save, so an author could edit blocks and hit Deploy: the button was enabled, and the deploy
   * shipped the PREVIOUS revision while the screen showed the new one. Same gap on close, which
   * discarded the blocks silently.
   *
   * `anyDirty` is the RENDER value — it drives the Deploy button's `disabled`, so it has to be
   * state. Guards must not use it: they can run in the same event sequence as the write that
   * dirtied the document (see the blocksDirtyRef comment above), and would then read a stale
   * `false`. `guardIsDirty()` reads the ref instead and is what every guard calls.
   *
   * The Speichern button deliberately stays on `isDirty`: it persists the Draft fields only, and
   * the blocks have their own Save in the Blocks tab.
   */
  const anyDirty = isDirty || blocksDirty;
  const guardIsDirty = () => isDirty || blocksDirtyRef.current;

  // Hat der Dialog „Affiliate-Links" gespeichert oder ausgespielt, sind Feld UND Revision hier
  // veraltet (die nächste Blocks-Speicherung liefe sonst in einen 409). Neu laden — aber nur, wenn
  // nichts Ungespeichertes offen ist; „Ändern…" ist bei offenen Änderungen ohnehin gesperrt.
  const affiliateStandRef = useRef(affiliateStand);
  useEffect(() => {
    if (affiliateStandRef.current === affiliateStand) return;
    affiliateStandRef.current = affiliateStand;
    if (profileId != null && !isDirty && !blocksDirtyRef.current) void load(profileId);
  }, [affiliateStand, profileId, isDirty, load]);

  const requestClose = () => {
    if (guardIsDirty()) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  // Blocks is the sole editable source once a profile is switched over — the stored HTML
  // is generated by the deploy renderer and must not be hand-edited.
  // ⚠ Muss VOR previewSrcDoc stehen: useMemo führt seine Funktion synchron beim Rendern aus, eine
  // spätere const-Deklaration läge in der temporalen Totzone.
  const blocksManaged = profile?.content_format === 'blocks';

  // Render holen, sobald der HTML-Reiter offen ist — und nur dann. Neu geholt wird erst, wenn sich
  // die Revision ändert (Speichern, Ausspielen, Wiederherstellen), weil genau dann ein anderer
  // Stand ausgespielt würde.
  useEffect(() => {
    // ⚠ Seit 2026-08-26 auch fuer HTML-Profile. Vorher zeigte die rechte Spalte dort die ROHE
    // Spalte (clientseitig) — mit Platzhaltern im Inhalt haette der Editor `{{bh:…}}` gezeigt,
    // waehrend die Live-Seite den Wert traegt. Eine Vorschau, der man nicht trauen kann, wird
    // nicht mehr geprueft; das ist teurer als der Fehler, den sie verdeckt.
    if (tab !== 'html' || !profile?.id) return;
    // Bei einem HTML-Profil haengt der Render am ENTWURF, nicht nur an der Revision — der Autor
    // tippt im Monaco und erwartet, dass die Vorschau folgt. `previewDraft` ist bereits die
    // entprellte Fassung, also kostet das keine Anfrage je Tastendruck.
    const entwurfHtml = blocksManaged ? '' : (previewDraft?.content_html ?? '');
    let summe = 0;
    for (let i = 0; i < entwurfHtml.length; i++)
      summe = (summe * 31 + entwurfHtml.charCodeAt(i)) | 0;
    const key = `${profile.id}:${profile.revision ?? '?'}:${entwurfHtml.length}:${summe}`;
    if (renderedForRef.current === key) return;
    let cancelled = false;
    // ⚠ Erst leeren, dann holen. Der Dialog wird NIE ausgehängt (broker-hub/index.tsx rendert ihn
    // ohne `open &&`-Guard), also überlebt `renderedHtml` den Profilwechsel: ohne dieses Leeren
    // zeigen Code-Ansicht und Vorschau für die Dauer des Renders die Seite des VORHERIGEN Brokers
    // unter dem neuen Titel — und nach jedem Speichern kurz den Stand von davor. Der Hinweis
    // darüber behauptet dabei „genau das, was beim Ausspielen auf die Seite geht". Leer und
    // ehrlich (mit „Render wird geholt …") schlägt gefüllt und falsch.
    setRenderedHtml(null);
    setRenderBusy(true);
    setRenderError(null);
    postData<{ html: string }>(
      `/api/broker-hub/profiles/${profile.id}/render-preview`,
      blocksManaged ? {} : { contentHtml: entwurfHtml },
    ).then((res) => {
      if (cancelled) return;
      setRenderBusy(false);
      if (res.success && res.data) {
        renderedForRef.current = key;
        setRenderedHtml(res.data.html);
        return;
      }
      // ⚠ KEIN stiller Rückfall auf `content_html`: das wäre wieder der alte Import, und der
      // Reiter behauptete erneut, das sei der ausgespielte Stand. Lieber nichts zeigen und sagen
      // warum.
      setRenderedHtml(null);
      setRenderError(res.error || 'Render fehlgeschlagen');
    });
    return () => {
      cancelled = true;
    };
  }, [tab, blocksManaged, profile?.id, profile?.revision, previewDraft?.content_html]);

  const previewSrcDoc = useMemo(() => {
    // ⚠ BEIDE Formate aus dem Server-Render. Fuer Blocks war das schon so (sonst zeigte der Code
    // links den ausgespielten Stand und die Vorschau daneben die alte Seite); fuer HTML ist es seit
    // der Platzhalter-Aufloesung Pflicht — clientseitig gaebe es keinen Weg, `{{bh:…}}` aufzuloesen,
    // ausser einem ZWEITEN Aufloeser im Frontend, und zwei Implementierungen derselben Regel
    // driften.
    const body = renderedHtml;
    if (!body) return null;
    const css = `${portalCss}\n${previewDraft?.content_css ?? ''}`;
    return `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>html,body{margin:0;background:#fff}${css}</style></head><body>${body}</body></html>`;
  }, [previewDraft, portalCss, blocksManaged, renderedHtml]);

  const save = async () => {
    if (!profile || !draft || !isDirty) return;
    setBusy(true);
    setMessage(null);
    const base = draftFromProfile(profile);
    const changed: Record<string, unknown> = {};
    (Object.keys(base) as (keyof Draft)[]).forEach((k) => {
      if (k === 'card_data') {
        // Object: compare by JSON, send the object itself (backend safeJsonStringify's it).
        // Normalize number fields (rating) back to real numbers on save.
        if (JSON.stringify(base.card_data) !== JSON.stringify(draft.card_data)) {
          changed.card_data = normalizeCardData(draft.card_data);
        }
        return;
      }
      if (base[k] !== draft[k]) changed[k] = draft[k] === '' ? null : draft[k];
    });
    const res = await fetchData<{ profile: FullProfile; warnings: string[] }>(
      `/api/broker-hub/profiles/${profile.id}`,
      { method: 'PUT', body: JSON.stringify({ ...changed, changeNote: changeNote || undefined }) },
    );
    if (res.success && res.data) {
      setProfile(res.data.profile);
      setDraft(draftFromProfile(res.data.profile));
      setChangeNote('');
      onChanged();
      fetchData<ProfileVersion[]>(`/api/broker-hub/profiles/${profile.id}/versions`).then((v) =>
        setVersions(v.success && v.data ? v.data : []),
      );
      const nochNichtLive =
        res.data.profile.status === 'live' ? ' — noch NICHT live: „Ausspielen" klicken.' : '';
      setMessage(
        res.data.warnings.length
          ? {
              severity: 'warning',
              text: `Gespeichert${nochNichtLive} ${res.data.warnings.join(', ')}`,
            }
          : {
              severity: nochNichtLive ? 'warning' : 'success',
              text: `Gespeichert (neue Version angelegt)${nochNichtLive}`,
            },
      );
      void ladeAusspiel(profile.id);
    } else {
      setMessage({ severity: 'error', text: res.error || 'Speichern fehlgeschlagen' });
    }
    setBusy(false);
  };

  const deploy = async () => {
    // guardIsDirty(), not isDirty and not anyDirty: a disabled button is a read-filter, this is
    // the enforcement, and it must see a block edit that was committed in the same event
    // sequence that got us here. With isDirty a deploy with unsaved blocks would ship the
    // previous revision; with anyDirty it would miss the just-committed panel field.
    if (!profile || guardIsDirty()) return;
    setBusy(true);
    setMessage(null);
    const res = await postData<{
      ok: boolean;
      action?: string;
      page_url?: string;
      warnings?: string[];
      error?: string;
    }>(`/api/broker-hub/profiles/${profile.id}/deploy`, {});
    if (res.success && (res.data as any)?.ok) {
      const d = res.data as any;
      const warn = d.warnings?.length ? ` — Hinweise: ${d.warnings.join('; ')}` : '';
      // ⚠ Der Cache-Hinweis gehoert an JEDE Erfolgsmeldung, nicht nur an den Takedown-Dialog.
      // Ohne ihn liest sich „Deployed" als „ab jetzt sichtbar", und wer die Seite gleich danach
      // aufruft, sieht die alte Fassung und meldet einen Fehlschlag, den es nicht gab. Gemessen
      // auf Boerse Express: `cache_duration: 1800` (30 min); 60 als konservative Obergrenze, weil
      // vor dem Portal noch weitere Schichten liegen koennen.
      const cacheHinweis = ' · Sichtbar wird das ggf. erst in bis zu 60 Min (Portal-Cache).';
      setMessage({
        severity: warn ? 'warning' : 'success',
        text: `Ausgespielt (${d.action}) → ${d.page_url}${warn}${cacheHinweis}`,
      });
      void ladeAusspiel(profile.id);
      onChanged();
    } else {
      setMessage({
        severity: 'error',
        text: `Ausspielen fehlgeschlagen: ${(res.data as any)?.error || res.error}`,
      });
    }
    setBusy(false);
  };

  const rollback = async (version: number) => {
    if (!profile) return;
    setBusy(true);
    const res = await postData<FullProfile>(`/api/broker-hub/profiles/${profile.id}/rollback`, {
      version,
    });
    if (res.success && res.data) {
      setProfile(res.data);
      setDraft(draftFromProfile(res.data));
      onChanged();
      fetchData<ProfileVersion[]>(`/api/broker-hub/profiles/${profile.id}/versions`).then((v) =>
        setVersions(v.success && v.data ? v.data : []),
      );
      setMessage({ severity: 'success', text: `Auf Version ${version} zurückgesetzt` });
      void ladeAusspiel(profile.id);
    } else {
      setMessage({ severity: 'error', text: res.error || 'Rollback fehlgeschlagen' });
    }
    setBusy(false);
  };

  const canDeploy =
    profile?.site_key && ['planned', 'live'].includes(profile.receiver_status ?? '');

  return (
    <Dialog
      fullScreen
      open={profileId != null}
      onClose={requestClose}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative' }} color="default" elevation={1}>
        <Toolbar variant="dense">
          <Typography variant="h6" sx={{ mr: 1 }}>
            {profile?.broker_name ?? '…'}
          </Typography>
          {profile && (
            <Chip
              size="small"
              label={profile.site_key ? profile.site_key.toUpperCase() : 'MASTER'}
              sx={{ mr: 1 }}
            />
          )}
          {ausspiel?.zustand && NICHT_AUSGESPIELT_TEXT[ausspiel.zustand] && (
            <Tooltip
              title={`${NICHT_AUSGESPIELT_TEXT[ausspiel.zustand]}${
                ausspiel.ausgespieltAm
                  ? ` Zuletzt ausgespielt: ${ausspiel.ausgespieltAm} (UTC).`
                  : ''
              }`}
            >
              <Chip size="small" color="warning" label="Nicht ausgespielt" sx={{ mr: 1 }} />
            </Tooltip>
          )}
          {profile?.page_url && (
            <IconButton
              size="small"
              href={profile.page_url}
              target="_blank"
              rel="noopener"
              title={profile.page_url}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          )}
          <Box flexGrow={1} />
          <TextField
            size="small"
            placeholder="Änderungsnotiz (optional)"
            value={changeNote}
            onChange={(e) => setChangeNote(e.target.value)}
            sx={{ width: 240, mr: 1 }}
          />
          <Button
            variant="contained"
            color="secondary"
            startIcon={busy ? <CircularProgress size={16} /> : <SaveOutlined />}
            disabled={!isDirty || busy}
            onClick={save}
            sx={{ mr: 1 }}
          >
            Speichern
          </Button>
          {canDeploy && (
            <Tooltip
              title={
                blocksDirty
                  ? 'Ungespeicherte Blöcke — erst im Blocks-Tab speichern, sonst wird der vorherige Stand ausgespielt'
                  : isDirty
                    ? 'Erst speichern, dann ausspielen'
                    : `Live auf ${profile?.site_key?.toUpperCase()} ausspielen`
              }
            >
              <span>
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadOutlined />}
                  disabled={anyDirty || busy}
                  // A deploy on a non-live profile is a TAKEDOWN — it unpublishes the portal
                  // page. Gate it behind a confirm so it can't happen by reflex (the trap that
                  // silently took VS Markets offline). Live deploys run straight through.
                  onClick={() => (draft?.status === 'live' ? deploy() : setConfirmTakedown(true))}
                  sx={{ mr: 1 }}
                >
                  Ausspielen
                </Button>
              </span>
            </Tooltip>
          )}
          <IconButton onClick={requestClose}>
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>

      {loading || !draft ? (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <CircularProgress />
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" height="100%" overflow="hidden">
          {message && (
            <Alert
              severity={message.severity}
              onClose={() => setMessage(null)}
              sx={{ borderRadius: 0 }}
            >
              {message.text}
            </Alert>
          )}
          <Box display="flex" flex={1} overflow="hidden">
            {/*
              LEFT: editor. Half the dialog for the HTML/CSS/field tabs, which are paired with the
              live preview on the right — but the FULL width on the Blocks tab, which lays out as
              canvas | properties panel and needs the room: the panel alone claims 380 px, so at
              50 % the writing area would have been ~200 px, against 578 px with inline chrome —
              i.e. the change would have made the editor markedly worse.

              Measured at 1440 px, writing area (block content, gutters excluded): 578 → 918 px.
              Quote that metric, not the 1026 px ProseMirror column beside it — the two differ by
              BlockNote's 2×54 px gutters and mixing them overstates the gain.
            */}
            <Box
              width={tab === 'blocks' ? '100%' : '50%'}
              display="flex"
              flexDirection="column"
              borderRight={tab === 'blocks' ? 'none' : '1px solid #e0e0e0'}
            >
              <Tabs
                value={tab}
                onChange={(_e, v) => {
                  setTab(v);
                  if (v === 'blocks') setBlocksMounted(true);
                }}
                variant="scrollable"
              >
                <Tab value="html" label="HTML" />
                <Tab value="css" label="CSS" />
                {blocksAllowed && <Tab value="blocks" label="Blocks" />}
                <Tab value="fields" label="Felder" />
                <Tab value="card" label="Übersichtskarte" />
                <Tab value="versions" label={`Versionen (${versions.length})`} />
              </Tabs>
              <Box flex={1} overflow="auto">
                {tab === 'html' && (
                  <Box height="100%" display="flex" flexDirection="column">
                    {blocksManaged && (
                      <Alert severity={renderError ? 'error' : 'info'} sx={{ borderRadius: 0 }}>
                        {renderError
                          ? `Der Render ist fehlgeschlagen: ${renderError}`
                          : renderBusy
                            ? 'Render wird geholt …'
                            : 'Aktueller Blocks-Render — genau das, was beim Ausspielen auf die Seite geht. Nur lesbar; bearbeitet wird im Reiter Blocks.'}
                      </Alert>
                    )}
                    <Box
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderBottom: '1px solid #eee',
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                      }}
                    >
                      <Button
                        size="small"
                        startIcon={
                          uploadingImg ? <CircularProgress size={14} /> : <ImageOutlined />
                        }
                        onClick={() => htmlImgFileRef.current?.click()}
                        disabled={uploadingImg || blocksManaged}
                      >
                        Bild einfügen
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        Lädt auf BunnyCDN und fügt ein &lt;img&gt; am Cursor ein.
                      </Typography>
                      <input
                        ref={htmlImgFileRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        hidden
                        onChange={onHtmlImageFile}
                      />
                    </Box>
                    <Box flex={1} minHeight={0}>
                      <Editor
                        height="100%"
                        defaultLanguage="html"
                        path={`profile-${profile?.id}.html`}
                        value={blocksManaged ? (renderedHtml ?? '') : draft.content_html}
                        onChange={(v) => {
                          if (blocksManaged) return; // schreibgeschützt, s. Alert oben
                          setDraft({ ...draft, content_html: v ?? '' });
                        }}
                        onMount={(ed) => {
                          htmlEditorRef.current = ed;
                        }}
                        options={{
                          minimap: { enabled: false },
                          wordWrap: 'on',
                          fontSize: 13,
                          scrollBeyondLastLine: false,
                          readOnly: blocksManaged,
                        }}
                      />
                    </Box>
                  </Box>
                )}
                {tab === 'css' && (
                  <Box height="100%" display="flex" flexDirection="column">
                    <Alert severity="info" sx={{ borderRadius: 0 }}>
                      Nur profil-spezifisches CSS. Das gemeinsame Design steht im Bereich{' '}
                      <strong>Design</strong> (Basis-CSS).
                    </Alert>
                    <Box flex={1}>
                      <Editor
                        height="100%"
                        defaultLanguage="css"
                        path={`profile-${profile?.id}.css`}
                        value={draft.content_css}
                        onChange={(v) => setDraft({ ...draft, content_css: v ?? '' })}
                        options={{
                          minimap: { enabled: false },
                          wordWrap: 'on',
                          fontSize: 13,
                          scrollBeyondLastLine: false,
                        }}
                      />
                    </Box>
                  </Box>
                )}
                {/* Blocks tab: mounted on first visit, then kept alive (hidden) so switching
                    tabs doesn't discard unsaved blocks. */}
                {blocksAllowed && blocksMounted && profile && (
                  <Box
                    sx={{
                      // flex + full height so the editor can fill the dialog instead of the fixed
                      // 60vh it used when it lived in a 50 % column; `hidden` because the three
                      // panes scroll individually.
                      display: tab === 'blocks' ? 'flex' : 'none',
                      height: '100%',
                      overflow: 'hidden',
                    }}
                  >
                    <React.Suspense
                      fallback={
                        <Box p={3} display="flex" justifyContent="center">
                          <CircularProgress />
                        </Box>
                      }
                    >
                      <BlocksEditor
                        // ⚠ Posten 2.6: die Partner-Domain und die Karte kommen aus dem ENTWURF.
                        // Ohne sie kennzeichnete die Vorschau gegen den gespeicherten Stand — wer
                        // eine neue Partner-Domain eintraegt und einen Knopf darauf setzt, saehe
                        // ihn UNMARKIERT und schloesse, die Kennzeichnung sei kaputt.
                        entwurfsFelder={{
                          affiliate_url: draft?.affiliate_url ?? '',
                          card_data: draft?.card_data ?? {},
                        }}
                        key={profile.id}
                        profileId={profile.id}
                        initialBlocks={profile.content_blocks ?? null}
                        revision={profile.revision ?? 0}
                        onSaved={(p) => {
                          // Blocks saves don't touch the Draft fields (HTML/CSS/Felder/Karte),
                          // so the draft stays valid — only the profile row advances
                          // (revision, content_blocks, content_format).
                          setProfile(p);
                          // Blocks-Speichern ist der Hauptweg — der Chip muss danach stimmen.
                          void ladeAusspiel(p.id);
                          onChanged();
                        }}
                        onDirtyChange={setBlocksDirtyBoth}
                        contentCss={draft.content_css}
                        siteKey={profile?.site_key ?? null}
                        /**
                         * „Überschreiben" im Aussehen-Abschnitt: die fertige Regel hinten an die
                         * Profil-CSS hängen und dorthin wechseln, damit der Autor sie sofort sieht
                         * und ändern kann. Angehängt, nie ersetzt — vorhandene Regeln sind fremde
                         * Arbeit. Der Wechsel auf den CSS-Reiter ist die halbe Funktion: ohne ihn
                         * landet der Text unsichtbar in einem Reiter, den niemand offen hat.
                         */
                        onAppendCss={(css) => {
                          setDraft((d) =>
                            d
                              ? {
                                  ...d,
                                  content_css:
                                    `${d.content_css.replace(/\s*$/, '')}\n\n${css}\n`.replace(
                                      /^\n+/,
                                      '',
                                    ),
                                }
                              : d,
                          );
                          setTab('css');
                        }}
                      />
                    </React.Suspense>
                  </Box>
                )}
                {tab === 'fields' && (
                  <Box p={2} display="flex" flexDirection="column" gap={2} maxWidth={620}>
                    <TextField
                      select
                      label="Status"
                      value={draft.status}
                      onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                      size="small"
                      helperText={
                        '„Offline" nimmt die Seite vom Portal und stellt das Original wieder her.'
                      }
                    >
                      <MenuItem value="live">Live</MenuItem>
                      <MenuItem value="draft">Entwurf</MenuItem>
                      <MenuItem value="taken_down">Offline (Takedown)</MenuItem>
                    </TextField>
                    <TextField
                      label="Seitentitel (SEO)"
                      value={draft.page_title}
                      onChange={(e) => setDraft({ ...draft, page_title: e.target.value })}
                      size="small"
                      helperText={
                        'Überschreibt beim Ausspielen den Seitentitel (title/og:title). Leer lassen = bestehenden Titel behalten.'
                      }
                    />
                    {/*
                      Schreibgeschützt seit 2026-10-23: ein Link-Wechsel betrifft auch die
                      Übersichtskarte und die Vergleichsseiten des Portals, und nur der Dialog
                      „Affiliate-Links" spielt alle zusammen aus. Zwei Schreibwege mit verschiedenem
                      Verhalten waren genau die Falle vom 20.10. (Freedom24).
                    */}
                    <Box display="flex" gap={1} alignItems="flex-start">
                      <TextField
                        label="Affiliate-Link (Profil-CTA)"
                        value={draft.affiliate_url}
                        size="small"
                        fullWidth
                        InputProps={{ readOnly: true }}
                        helperText={
                          'Ändern über „Affiliate-Links" — dort werden alle Portale zusammen gepflegt und Profil, Karte und Vergleichsseiten zusammen ausgespielt.'
                        }
                      />
                      {onAffiliateLinks && profile?.broker_id ? (
                        <Tooltip
                          title={anyDirty ? 'Erst die offenen Änderungen hier speichern.' : ''}
                        >
                          <span>
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={anyDirty || busy}
                              onClick={() =>
                                onAffiliateLinks(Number(profile.broker_id), profile.broker_name)
                              }
                              sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
                            >
                              Ändern…
                            </Button>
                          </span>
                        </Tooltip>
                      ) : null}
                    </Box>
                    <Alert severity="info" sx={{ borderRadius: 1 }}>
                      Affiliate-Link, Button-Text und Risikohinweis der{' '}
                      <strong>Übersichtskarte</strong> werden im Tab{' '}
                      <strong>Übersichtskarte</strong> bearbeitet.
                    </Alert>
                    <Typography variant="caption" color="text.secondary">
                      Import-Quelle: {profile?.imported_from ?? '—'} · Seite:{' '}
                      {profile?.page_url ?? '—'}
                    </Typography>
                  </Box>
                )}
                {tab === 'card' && (
                  <CardEditor
                    value={draft.card_data}
                    // Functional: apply the updater to the LATEST draft.card_data so a late async
                    // image upload can't revert a concurrent edit to another draft field.
                    onChange={(updater) =>
                      setDraft((prev) =>
                        prev ? { ...prev, card_data: updater(asCardData(prev.card_data)) } : prev,
                      )
                    }
                    siteKey={profile?.site_key ?? null}
                    siteBaseUrl={profile?.base_url ?? null}
                    zentraleZahlungsmittel={profile?.broker_card_payments ?? null}
                  />
                )}
                {tab === 'versions' && (
                  <TableContainer>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Version</TableCell>
                          <TableCell>Notiz</TableCell>
                          <TableCell>Von</TableCell>
                          <TableCell align="right">Aktion</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {versions.map((v, idx) => (
                          <TableRow key={v.id} hover>
                            <TableCell>v{v.version}</TableCell>
                            <TableCell>{v.change_note ?? '—'}</TableCell>
                            <TableCell>{v.created_by ?? '—'}</TableCell>
                            <TableCell align="right">
                              {idx === 0 ? (
                                <Chip size="small" label="Aktuell" variant="outlined" />
                              ) : v.restorable === false ? (
                                // Der Server hat die Zeile gesperrt (Vor-Blocks-Stand). Die
                                // Begründung kommt mit — ein grauer Knopf ohne Grund erzeugt nur
                                // eine Rückfrage. `span` als Tooltip-Kind: ein disabled Button
                                // feuert keine Maus-Events.
                                <Tooltip
                                  title={v.restore_blocked_reason ?? ''}
                                  arrow
                                  placement="left"
                                >
                                  <span>
                                    <Button size="small" startIcon={<History />} disabled>
                                      Wiederherstellen
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : (
                                <Button
                                  size="small"
                                  startIcon={<History />}
                                  disabled={busy}
                                  onClick={() => rollback(v.version)}
                                >
                                  Wiederherstellen
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Box>

            {/*
              RIGHT: live preview — for the HTML/CSS/field tabs, which edit `content_html`
              directly, so rendering it is a true preview.

              NOT shown on the Blocks tab, and that is a bug fix rather than a layout choice: this
              pane showed a stale page that looked entirely plausible while the author edited
              blocks beside it.

              ⚠ Korrektur 2026-08-10: hier stand, `content_html` sei bei einem Blocks-Profil "the
              LAST DEPLOYED render". Das ist falsch — der Deploy schreibt nichts zurück, die Spalte
              ist der eingefrorene Import von vor der Umstellung (gemessen: `<script` drin,
              0× `bh-blocks`). Auf dem HTML-Reiter speist sich diese Spalte deshalb jetzt aus
              `renderedHtml`, dem echten Server-Render.

              There is deliberately no live replacement. A client-side render was tried and
              measured wrong: both `blocksToHTMLLossy` and `blocksToFullHTML` emit an EMPTY element
              for every React block spec, because rendering those to a string needs jsdom +
              react-dom — which is exactly what `@blocknote/server-util` does in the forked render
              worker and what a browser has no equivalent for. Since the canvas itself now shows
              the page (the inline chrome is gone), the authoritative server render on the
              "Vorschau" button is the only preview, and the width goes to the canvas instead.
            */}
            <Box width="50%" display={tab === 'blocks' ? 'none' : 'flex'} flexDirection="column">
              <Box
                px={2}
                py={1}
                display="flex"
                alignItems="center"
                borderBottom="1px solid #e0e0e0"
              >
                <Typography variant="subtitle2">Live-Vorschau (echtes Portal-Design)</Typography>
                <InfoHint text="Zeigt das Profil mit dem zentralen Basis-CSS + Portal-Anpassung, wie es live aussieht. Aktualisiert sich beim Tippen." />
              </Box>
              <Box flex={1} bgcolor="#fff">
                {previewSrcDoc ? (
                  <iframe
                    title="Profil-Vorschau"
                    srcDoc={previewSrcDoc}
                    sandbox="allow-same-origin"
                    style={{ width: '100%', height: '100%', border: 0, background: '#fff' }}
                  />
                ) : (
                  <Box p={3}>
                    <HelpLegend items={EDITOR_HELP} storageKey="editor" defaultOpen />
                    <Typography color="text.secondary">
                      Kein Inhalt — im HTML-Tab einfügen.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      <Dialog open={confirmDiscard} onClose={() => setConfirmDiscard(false)}>
        <DialogTitle>Ungespeicherte Änderungen verwerfen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {blocksDirty && isDirty
              ? 'Es gibt ungespeicherte Blöcke und ungespeicherte Feldänderungen.'
              : blocksDirty
                ? 'Der Blocks-Editor enthält ungespeicherte Änderungen.'
                : 'Es gibt ungespeicherte Änderungen.'}{' '}
            Beim Schließen gehen sie verloren.
            {blocksDirty && (
              <>
                <br />
                <br />
                Blöcke werden über <strong>Speichern im Blocks-Tab</strong> gesichert — der
                Speichern-Knopf oben sichert nur HTML, CSS, Felder und Karte.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDiscard(false)}>Zurück zum Bearbeiten</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setConfirmDiscard(false);
              onClose();
            }}
          >
            Verwerfen &amp; schließen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmTakedown} onClose={() => setConfirmTakedown(false)}>
        <DialogTitle>Seite offline nehmen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Der Status dieses Profils ist{' '}
            <strong>{STATUS_STYLES[draft?.status ?? '']?.label ?? draft?.status}</strong>. Ein
            Ausspielen nimmt die Seite <strong>{profile?.page_url ?? 'auf dem Portal'}</strong> vom
            Netz (unpublish).{' '}
            {profile?.page_type === 'compare'
              ? 'Die Seite bleibt im Portal-CMS bestehen und lässt sich jederzeit wieder live schalten — gelöscht wird nichts.'
              : 'Das Original-Template wird wiederhergestellt, und sie erscheint nicht mehr auf der Übersicht.'}
            <br />
            <br />
            Zum Veröffentlichen zuerst den Status auf <strong>Live</strong> setzen und speichern.
          </DialogContentText>
          <Alert severity="info" sx={{ mt: 2 }}>
            <strong>Bis zu 60 Minuten Verzögerung.</strong> Die Portale liefern Seiten aus einem
            Cache aus; nach dem Ausspielen kann die alte Fassung noch eine Weile erscheinen — auch
            beim Offline-Nehmen. Gemessen auf Börse Express: 30 Minuten Cache-Dauer. Das ist normal
            und kein Fehlschlag des Ausspielens.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTakedown(false)}>Abbrechen</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setConfirmTakedown(false);
              deploy();
            }}
          >
            Ja, offline nehmen
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ProfileEditorDialog;
